import styled from '@emotion/styled';

import { Badge } from '@/components/common/Badge';
import { screen } from '@/components/common/breakpoints';
import { RichHeading } from '@/components/common/Heading';
import { Icon } from '@/components/common/Icon';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Surface } from '@/components/common/Surface';
import { Title } from '@/components/common/Title';
import { TitleWithIcon } from '@/components/common/TitleWithIcon';
import { Stack } from '@/components/layout/Stack';

import type {
  DeliveryDividerProps,
  DeliveryModelProps,
  DeliverySectionProps,
} from './types';

const DeliverySection = ({ content, heading, tag }: DeliverySectionProps) => (
  <InnerSection>
    <HeadingLevel>
      <Stack spacing="--space-component-xl">
        <HeadingRow>
          <Title heading={heading} variant="block" size="sm" />
          {tag?.tagText && (
            <Badge
              bgColour={{
                _type: 'surfaceColour',
                value: 'rgb(230, 232, 254)',
                token: '--surface-primary-subtle',
              }}
              margin={{ base: '0', sm: '-7px -6px -6px 0' }}
              size={{ base: 'sm', sm: 'md' }}
            >
              {tag.tagText}
            </Badge>
          )}
        </HeadingRow>
        {content?.map((item) => (
          <HeadingLevel key={item._key}>
            <StyledTitleWithIcon
              variant="component"
              size="lg"
              alignment="left"
              heading={item.title.heading}
              subheading={item.title.subheading}
              iconName={item?.icon?.name}
            />
          </HeadingLevel>
        ))}
      </Stack>
    </HeadingLevel>
  </InnerSection>
);

const DeliveryDivider = ({ icon }: DeliveryDividerProps) => {
  return (
    <DividerContainer>
      <DividingLine />
      <Icon size="16px" icon={icon?.name} colour="--icon-warm-base" />
      <DividingLine />
    </DividerContainer>
  );
};

export const DeliveryModel = ({
  heading,
  sections,
  footer,
}: DeliveryModelProps) => {
  return (
    <Stack spacing="--space-component-xxl">
      <HeadingLevel>
        <TitleWrapper>
          <Title variant="block" size="md" heading={heading} />
        </TitleWrapper>
        <Surface
          borderRadius="--radius-component-xl"
          padding={false}
          border="--border-warm-base"
        >
          {sections?.map((sect) => {
            if (sect._type === 'section')
              return <DeliverySection {...sect} key={sect._key} />;
            if (sect._type === 'divider')
              return <DeliveryDivider {...sect} key={sect._key} />;
            return null;
          })}

          {footer?.heading?.length && (
            <FooterSection>
              <Stack spacing="--space-component-md">
                <HeadingLevel>
                  <RichHeading heading={footer.heading} size="heading-sm" />
                </HeadingLevel>
                <Paragraph size="body-md" colour="--text-warm-base">
                  {footer.subheading}
                </Paragraph>
              </Stack>
            </FooterSection>
          )}
        </Surface>
      </HeadingLevel>
    </Stack>
  );
};

const TitleWrapper = styled.div`
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: var(--font-weight-semibold);
  }
`;

const InnerSection = styled.div`
  padding: var(--space-component-xl);
  ${screen.sm} {
    padding: var(--space-component-xl) var(--space-component-xxl);

    &:first-child {
      padding-top: var(--space-component-xxl);
    }
    &:last-child {
      padding-bottom: var(--space-component-xxl);
    }
  }
`;

const FooterSection = styled(InnerSection)`
  ${screen.sm} {
    text-align: center;
  }
`;

const HeadingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-component-md);

  ${screen.sm} {
    gap: var(--space-component-lg);
  }
  align-items: start;

  > :first-child {
    order: 1;
    flex: 1;
  }

  ${screen.sm} {
    flex-direction: row;
    > :first-child {
      order: 0;
    }
  }
`;

const StyledTitleWithIcon = styled(TitleWithIcon)`
  padding-left: var(--space-component-sm);
`;

const DividerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > div {
    ${screen.sm} {
      margin: 0;
    }
  }
`;

const DividingLine = styled.hr`
  all: unset;
  height: 1px;
  background-color: var(--border-warm-base);

  &:first-of-type {
    width: 28px;
  }
  &:last-of-type {
    width: 100%;
  }
  ${screen.sm} {
    flex: 1;
  }
`;
